import React from 'react';
import { Line, LineChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import Summary from '@/components/Summary';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CircleAlert } from 'lucide-react';
interface PerformanceComparisonProps {
  selected?: boolean;
  onSelectChange?: (checked: boolean) => void;
}

const PerformanceComparison: React.FC<any> = ({ selected = true, onSelectChange, totalData }) => {
  const productInfo = totalData?.product_info || [];
  const product1 = productInfo[0] || {};
  const product2 = productInfo[1] || {};
  // 图表数据
  const chartData = [
    { month: '起始', 新能源: 100, 消费: 100 },
    { month: '1月', 新能源: 108, 消费: 102 },
    { month: '2月', 新能源: 115, 消费: 105 },
    { month: '3月', 新能源: 112, 消费: 108 },
    { month: '6月', 新能源: 120, 消费: 110 },
    { month: '1年', 新能源: 118, 消费: 112 }
  ];

  // 收益数据
  const performanceData = [
    { period: '近1周', 新能源: '+3.24%', 消费: '+1.18%', 差值: '+1.18%' },
    { period: '近1月', 新能源: '+3.24%', 消费: '+1.18%', 差值: '+1.18%' },
    { period: '近3月', 新能源: '+3.24%', 消费: '+1.18%', 差值: '+1.18%' },
    { period: '近1年', 新能源: '-12.45%', 消费: '-12.45%', 差值: '-12.45%' },
    { period: '成立以来', 新能源: '-12.45%', 消费: '-12.45%', 差值: '-12.45%' }
  ];

  const timeRange = '本年收益';

  const handleValueChange = (value: string) => {
    console.log(value);
  };

  return (
    <div className="mb-3 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3">
      {/* 标题行 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selected}
            onChange={e => onSelectChange?.(e.target.checked)}
            className="h-4 w-4 rounded border border-gray-300 checked:border-blue-600 checked:bg-blue-600"
          />
          <span className="text-base font-semibold text-gray-900">收益表现对比</span>
        </div>
        <div className="flex items-center gap-2">
          <Select onValueChange={handleValueChange}>
            <SelectTrigger className="h-auto w-auto border-0 bg-transparent p-0 shadow-none focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-gray-200 bg-white shadow-md">
              <SelectItem value="近1月">近1月</SelectItem>
              <SelectItem value="近3月">近3月</SelectItem>
              <SelectItem value="近半年">近半年</SelectItem>
              <SelectItem value="近1年">近1年</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* 图表区域 */}
      <div className="flex flex-col gap-3">
        {/* 图表 */}
        <div className="flex flex-col gap-1">
          <div className="flex justify-end gap-4">
            <div className="flex items-center gap-1">
              <div className="h-2 w-2 rounded-sm bg-blue-600"></div>
              <span className="text-xs text-gray-900">新能源</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-2 w-2 rounded-sm bg-orange-500"></div>
              <span className="text-xs text-gray-900">消费</span>
            </div>
          </div>

          <div className="relative h-[178px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" opacity={0.8} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10, fill: '#595959' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[100, 120]}
                  tick={{ fontSize: 10, fill: '#595959' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Line
                  type="monotone"
                  dataKey="新能源"
                  stroke="#5959EB"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 4, stroke: '#5959EB', strokeWidth: 2 }}
                />
                <Line
                  type="monotone"
                  dataKey="消费"
                  stroke="#FA8C16"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 4, stroke: '#FA8C16', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 收益数据表格 */}
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="flex">
            {/* 时间段列 */}
            <div className="flex w-full flex-col">
              <div className="rounded-tl-lg bg-gray-100 px-2 py-1">
                <span className="text-xs font-semibold text-gray-900">时间段</span>
              </div>
              {performanceData.map((item, index) => (
                <div key={index} className="border-t border-gray-200 px-2 py-1">
                  <span className="text-xs text-gray-900">{item.period}</span>
                </div>
              ))}
            </div>

            {/* 招商新能源列 */}
            <div className="flex w-full min-w-0 flex-col bg-blue-50/50">
              <div className="truncate rounded-tr-lg bg-blue-100/10 px-2 py-1">
                <span className="text-xs font-semibold text-blue-600" title={product1.产品名称}>
                  {product1.产品名称}
                </span>
              </div>
              {performanceData.map((item, index) => (
                <div key={index} className="border-t border-gray-200 px-2 py-1">
                  <span
                    className={`text-xs font-semibold ${
                      item.新能源.startsWith('+') ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    {item.新能源}
                  </span>
                </div>
              ))}
            </div>

            {/* 易方达消费列 */}
            <div className="flex w-full min-w-0 flex-col bg-orange-50/60">
              <div className="truncate bg-orange-100/10 px-2 py-1">
                <span className="text-xs font-semibold text-orange-600" title={product2.产品名称}>
                  {product2.产品名称}
                </span>
              </div>
              {performanceData.map((item, index) => (
                <div key={index} className="border-t border-gray-200 px-2 py-1">
                  <span
                    className={`text-xs font-semibold ${
                      item.消费.startsWith('+') ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    {item.消费}
                  </span>
                </div>
              ))}
            </div>

            {/* 差值列 */}
            <div className="flex w-full flex-col rounded-tr-lg bg-blue-50/30">
              <div className="flex items-center justify-center rounded-tr-lg bg-blue-100/5 p-[8px]">
                <span className="mr-1 text-xs font-semibold text-blue-600">差值</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help overflow-hidden text-ellipsis text-sm font-normal text-[#666666]">
                        <CircleAlert size={14} className="text-blue-600" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">3333</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {performanceData.map((item, index) => (
                <div key={index} className="border-t border-gray-200 px-2 py-1">
                  <span
                    className={`text-xs font-semibold ${
                      item.差值.startsWith('+') ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    {item.差值}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI总结部分 */}
      <Summary title="AI总结-历史业绩" content={totalData?.历史业绩} isShowSuggestions={false} />
    </div>
  );
};

export default PerformanceComparison;
